import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveLLMInstructions, generateDomainKnowledge, DomainKnowledgeResponse } from '../services/databaseService';
import { getConnectionId, clearAllStorage } from '../utils/storage';
import { notifyParentSessionExpired } from '../utils/embed';
import Toast from '../components/Toast';
import ThemeToggle from '../components/ThemeToggle';

interface ToastMessage {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

const LLMInstructions: React.FC = () => {
  const navigate = useNavigate();
  const [instructions, setInstructions] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDomain, setIsLoadingDomain] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [progress, setProgress] = useState(0);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Simulated progress while loading domain knowledge
  useEffect(() => {
    if (isLoadingDomain) {
      setProgress(0);
      progressIntervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev < 30) return prev + Math.random() * 3 + 1;      // Fast: analyzing schema
          if (prev < 60) return prev + Math.random() * 2 + 0.5;    // Medium: generating knowledge
          if (prev < 85) return prev + Math.random() * 0.8 + 0.2;  // Slow: processing insights
          if (prev < 92) return prev + Math.random() * 0.3 + 0.05; // Very slow: finalizing
          return prev; // Stall near 92% waiting for API
        });
      }, 200);
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      // Snap to 100% briefly then reset
      if (progress > 0) {
        setProgress(100);
        const resetTimer = setTimeout(() => setProgress(0), 600);
        return () => clearTimeout(resetTimer);
      }
    }
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingDomain]);

  const getProgressPhaseLabel = (pct: number): string => {
    if (pct < 15) return 'Connecting to database...';
    if (pct < 30) return 'Analyzing database schema...';
    if (pct < 55) return 'Generating SQL instruction set...';
    if (pct < 75) return 'Detecting table relationships...';
    if (pct < 90) return 'Classifying column roles...';
    if (pct < 100) return 'Almost there...';
    return 'Complete!';
  };

  // Logout handler
  const handleLogout = () => {
    clearAllStorage();
    // Embedded in the Rage app: let the parent know the session ended instead
    // of silently dropping the user into the standalone /login flow.
    notifyParentSessionExpired();
    navigate('/login');
  };

  const formatDomainKnowledge = (data: DomainKnowledgeResponse): string => {
    const { instruction_set } = data;

    let formatted = `=== SQL Instruction Set v${instruction_set.version} ===\n\n`;

    // SQL Rules
    formatted += `SQL Safety Rules:\n`;
    formatted += `- Allowed operations: ${instruction_set.sql_rules.allowed_operations.join(', ')}\n`;
    formatted += `- Forbidden operations: ${instruction_set.sql_rules.forbidden_operations.join(', ')}\n`;
    formatted += `- SELECT * forbidden: ${instruction_set.sql_rules.no_select_star ? 'Yes' : 'No'}\n`;
    formatted += `- Default LIMIT: ${instruction_set.sql_rules.default_limit}\n`;
    formatted += `- Avoid sensitive columns: ${instruction_set.sql_rules.avoid_sensitive_columns.join(', ')}\n\n`;

    // Join Rules
    formatted += `Join Rules:\n`;
    formatted += `- Use foreign keys for joins: ${instruction_set.join_rules.use_foreign_keys_for_joins ? 'Yes' : 'No'}\n`;
    formatted += `- Join type preference: ${instruction_set.join_rules.join_type_preference}\n`;
    formatted += `- Max join depth: ${instruction_set.join_rules.max_join_depth}\n\n`;
    formatted += `\n`;

    // Column Roles
    formatted += `Column Role Hints:\n`;
    formatted += `- Metric patterns: ${instruction_set.column_roles.metric_patterns.join(', ')}\n`;
    formatted += `- Timestamp patterns: ${instruction_set.column_roles.timestamp_patterns.join(', ')}\n`;
    formatted += `- Category patterns: ${instruction_set.column_roles.category_patterns.join(', ')}\n`;
    formatted += `- Identifier patterns: ${instruction_set.column_roles.identifier_patterns.join(', ')}\n`;
    formatted += `\n`;

    // Query Structure Rules
    formatted += `Query Structure Rules:\n`;
    formatted += `- Always use aliases: ${instruction_set.query_structure_rules.always_use_aliases ? 'Yes' : 'No'}\n`;
    formatted += `- GROUP BY required for aggregates: ${instruction_set.query_structure_rules.group_by_required_for_aggregates ? 'Yes' : 'No'}\n`;
    formatted += `- Limit large results: ${instruction_set.query_structure_rules.limit_large_results ? 'Yes' : 'No'}\n\n`;

    // NL Query Rules
    formatted += `Natural Language Query Rules:\n`;
    formatted += `- Aggregation keywords: ${instruction_set.nl_query_rules.aggregation_keywords.join(', ')}\n`;
    formatted += `- Trend keywords: ${instruction_set.nl_query_rules.trend_keywords.join(', ')}\n`;
    formatted += `- Comparison keywords: ${instruction_set.nl_query_rules.comparison_keywords.join(', ')}\n`;
    formatted += `- List keywords: ${instruction_set.nl_query_rules.list_keywords.join(', ')}\n`;

    return formatted;
  };

  const loadDomainKnowledge = async () => {
    if (!tenantId) return;

    setIsLoadingDomain(true);

    try {
      const domainData = await generateDomainKnowledge(tenantId);
      const formattedText = formatDomainKnowledge(domainData);
      setInstructions(formattedText);
      setToast({ message: 'Domain knowledge loaded successfully!', type: 'success' });
    } catch (error) {
      console.error('Error loading domain knowledge:', error);
      setToast({
        message: error instanceof Error ? error.message : 'Failed to load domain knowledge',
        type: 'error'
      });
    } finally {
      setIsLoadingDomain(false);
    }
  };

  useEffect(() => {
    const savedConnectionId = getConnectionId();
    if (!savedConnectionId) {
      // If no connection_id found, redirect back to database config
      navigate('/database-config');
      return;
    }
    setTenantId(savedConnectionId);

    // Automatically load domain knowledge when page loads
    const loadDomainKnowledgeOnMount = async () => {
      setIsLoadingDomain(true);
      try {
        const domainData = await generateDomainKnowledge(savedConnectionId);
        const formattedText = formatDomainKnowledge(domainData);
        setInstructions(formattedText);
        setToast({ message: 'Domain knowledge loaded successfully!', type: 'success' });
      } catch (error) {
        console.error('Error loading domain knowledge:', error);
        setToast({
          message: error instanceof Error ? error.message : 'Failed to load domain knowledge',
          type: 'error'
        });
      } finally {
        setIsLoadingDomain(false);
      }
    };

    loadDomainKnowledgeOnMount();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!instructions.trim()) {
      setToast({ message: 'Instructions are required', type: 'warning' });
      return;
    }

    setIsSubmitting(true);

    try {
      if (!tenantId) {
        setToast({
          message: 'No connection reference found. Please configure database first.',
          type: 'error'
        });
        return;
      }

      const result = await saveLLMInstructions(tenantId, instructions);

      setToast({
        message: result.message || 'Instructions saved successfully!',
        type: 'success'
      });

      // Redirect to table column config after successful submission
      setTimeout(() => {
        navigate('/table-column-config');
      }, 2000);
    } catch (error) {
      console.error('Error saving instructions:', error);
      setToast({
        message: error instanceof Error ? error.message : 'Failed to save instructions',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (value: string) => {
    setInstructions(value);
  };

  // Show loading while checking for tenant_id or loading domain knowledge
  if (tenantId === null || isLoadingDomain) {
    const displayProgress = Math.min(Math.round(progress), 100);
    const phaseLabel = tenantId === null ? 'Checking configuration...' : getProgressPhaseLabel(displayProgress);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-xl relative">
          <div className="absolute top-0 right-0">
            <ThemeToggle />
          </div>
          {/* Glass Card */}
          <div
            className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl border border-white/50 dark:border-gray-700/50 p-10"
            style={{
              boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255,255,255,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}
          >
            {/* Animated Icon */}
            <div className="flex justify-center mb-5">
              <div
                className="relative inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-2xl"
                style={{ boxShadow: '0 8px 32px rgba(99, 102, 241, 0.35)' }}
              >
                <svg className="w-9 h-9 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014.846 21H9.154a3.374 3.374 0 00-2.14-1.21l-.548-.547z" />
                </svg>
                {/* Icon ring animation */}
                <div className="absolute inset-0 rounded-2xl border-2 border-white/20 animate-ping" style={{ animationDuration: '2s' }} />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-extrabold text-center bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
              Loading SQL Instruction Set
            </h2>
            <p className="text-center text-sm text-gray-400 dark:text-gray-500 mb-8">
              Analyzing your database schema
            </p>

            {/* ── Progress Bar ── */}
            <div className="relative px-8 mb-6 pt-12">
              {/* Badge (floating above) */}
              <div className="relative w-full" style={{ height: 0 }}>
                <div
                  className="progress-badge"
                  style={{ left: `${Math.max(displayProgress, 6)}%` }}
                >
                  {displayProgress}%
                </div>
              </div>

              {/* Outer skeuomorphic shell */}
              <div className="progress-bar-wrapper">
                {/* Ornament left */}
                <svg className="progress-ornament left" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l1.09 3.41L16.18 4l-1.64 3.09L18 8.73l-3.54.29L12 12l-2.46-2.98L6 8.73l3.46-1.64L7.82 4l3.09 1.41z" />
                </svg>

                {/* Track */}
                <div className="progress-bar-track">
                  {/* Fill */}
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${displayProgress}%` }}
                  >
                    {/* Shimmer sweep */}
                    <div className="progress-bar-shimmer" />
                  </div>
                </div>

                {/* Ornament right */}
                <svg className="progress-ornament right" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l1.09 3.41L16.18 4l-1.64 3.09L18 8.73l-3.54.29L12 12l-2.46-2.98L6 8.73l3.46-1.64L7.82 4l3.09 1.41z" />
                </svg>

                {/* Knob */}
                <div
                  className="progress-knob"
                  style={{ left: `${Math.max(displayProgress, 3)}%` }}
                />
              </div>
            </div>

            {/* Phase Label */}
            <p className="text-center text-sm font-semibold text-gray-500 dark:text-gray-400 tracking-wide">
              {phaseLabel}
            </p>

            {/* Subtle info text */}
            <p className="text-center text-xs text-gray-400 dark:text-gray-600 mt-2">
              This may take a moment depending on your database size
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="max-w-5xl mx-auto px-4 py-8 sm:px-6 lg:px-8 relative">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl mb-4 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014.846 21H9.154a3.374 3.374 0 00-2.14-1.21l-.548-.547z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            LLM Instructions
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Provide detailed instructions for the Language Learning Model to guide its behavior and responses
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit} className="p-8">
            <div className="space-y-6">
              {/* Instructions Section */}
              <div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                  <label htmlFor="instructions" className="block text-base font-semibold text-gray-700 dark:text-gray-300">
                    Database Knowledge *
                  </label>
                  <button
                    type="button"
                    onClick={loadDomainKnowledge}
                    disabled={isLoadingDomain || !tenantId}
                    className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-lg shadow-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95"
                  >
                    {isLoadingDomain ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </>
                    ) : (
                      <>
                        <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014.846 21H9.154a3.374 3.374 0 00-2.14-1.21l-.548-.547z" />
                        </svg>
                        Load Database Knowledge Using AI
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  id="instructions"
                  value={instructions}
                  onChange={(e) => handleInputChange(e.target.value)}
                  placeholder="Enter detailed instructions for the Language Learning Model here...

Example:
- Always respond in a professional and helpful manner
- Focus on providing accurate data analysis
- When querying databases, explain the reasoning behind your queries
- If unsure about data interpretation, ask for clarification
- Prioritize data privacy and security in all responses"
                  rows={15}
                  disabled={isSubmitting || isLoadingDomain}
                  className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical min-h-[400px] dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                />
                <div className="flex justify-between items-center mt-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Provide clear, specific instructions to help the LLM understand your requirements
                  </p>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {instructions.length} characters
                  </span>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex gap-3 flex-1">
                  {/* <button
                    type="button"
                    onClick={() => navigate('/database-config')}
                    disabled={isSubmitting}
                    className="flex-1 sm:flex-initial inline-flex items-center justify-center px-5 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                  </button> */}
                  {/* <button
                    type="button"
                    onClick={handleLogout}
                    className="flex-1 sm:flex-initial inline-flex items-center justify-center px-5 py-3 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-800 rounded-lg shadow-sm hover:bg-red-100 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Logout
                  </button> */}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || !instructions.trim()}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-lg shadow-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving Instructions...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Save Instructions
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LLMInstructions;
